/**
 * Central enum definitions for Rules V2 Engine
 */

/**
 * Query Operators
 *
 * Defines the comparison operators that can be used in rule conditions.
 * Operators are grouped by condition type (selection-based vs text-based).
 */
export enum QueryOperator {
  // Selection operators (for Ticket Channel, Topic, Assignment Type)
  IS = "is",
  IS_NOT = "is_not",

  // Text operators (for Subject Line, Message Body, Sent From Email, Received At Email)
  CONTAINS = "contains",
  DOES_NOT_CONTAIN = "does_not_contain",
  IS_EXACTLY = "is_exactly",
  DOES_NOT_EQUAL = "does_not_equal",

  // Time-based status operator (for Ticket Status "has been" condition only)
  HAS_BEEN = "has_been",
}

// Operators for selection-based conditions (Ticket Channel, Topic, Assignment Type)
export const selectionQueryOperators = [
  QueryOperator.IS,
  QueryOperator.IS_NOT,
] as const;
export type SelectionQueryOperator = (typeof selectionQueryOperators)[number];

// Operators for text-based conditions (Subject Line, Message Body, Sent From Email, Received At Email)
export const textQueryOperators = [
  QueryOperator.CONTAINS,
  QueryOperator.DOES_NOT_CONTAIN,
  QueryOperator.IS_EXACTLY,
  QueryOperator.DOES_NOT_EQUAL,
] as const;
export type TextQueryOperator = (typeof textQueryOperators)[number];

/**
 * Date Query Operators
 *
 * Defines comparison operators for date-based conditions (e.g., Last Order Time).
 */
export enum DateQueryOperator {
  WITHIN_LAST = "within_last",
  ON_OR_BEFORE = "on_or_before",
  ON_OR_AFTER = "on_or_after",
  BETWEEN = "between",
  NEVER = "never",
}

export const dateQueryOperators = [
  DateQueryOperator.WITHIN_LAST,
  DateQueryOperator.ON_OR_BEFORE,
  DateQueryOperator.ON_OR_AFTER,
  DateQueryOperator.BETWEEN,
  DateQueryOperator.NEVER,
] as const;
export type DateQueryOperatorType = (typeof dateQueryOperators)[number];

/**
 * Numeric Query Operators
 *
 * Defines comparison operators for numeric conditions (e.g., order value, item count).
 */
export enum NumericQueryOperator {
  GREATER_THAN = "greater_than",
  LESS_THAN = "less_than",
  EQUAL_TO = "equal_to",
  BETWEEN = "between",
}

export const numericQueryOperators = [
  NumericQueryOperator.GREATER_THAN,
  NumericQueryOperator.LESS_THAN,
  NumericQueryOperator.EQUAL_TO,
  NumericQueryOperator.BETWEEN,
] as const;
export type NumericQueryOperatorType = (typeof numericQueryOperators)[number];

/**
 * Rule Trigger Types
 *
 * Defines when a rule should be evaluated and executed.
 * Each trigger type represents a specific event in the system.
 */
export enum RuleTriggerType {
  CONVERSATION_CREATED = "conversation_created",
  CONVERSATION_MESSAGE_CREATED = "conversation_message_created",
  MERCHANT_MESSAGE_CREATED = "merchant_message_created",
  MESSAGE_RECEIVED = "message_received",
  CONVERSATION_STATUS_CHANGED = "conversation_status_changed",
  TAG_ADDED = "tag_added",
  TAG_REMOVED = "tag_removed",
  ASSIGNMENT_CHANGED = "assignment_changed",
  CONVERSATION_PRIORITY_CHANGED = "conversation_priority_changed",
  CONVERSATION_READ = "conversation_read",
  THREAD_CREATED = "thread_created",
  CHAT_CONVERTED_TO_EMAIL = "chat_converted_to_email",
}

/**
 * Rule Condition Types
 *
 * Defines the types of conditions that can be evaluated in a rule.
 * All conditions must pass (AND logic) or at least one must pass (OR logic)
 * depending on the condition group configuration.
 */
export enum RuleConditionType {
  TICKET_CHANNEL = "ticket_channel",
  TICKET_STATUS = "ticket_status",
  TOPIC = "topic",
  SENT_FROM_EMAIL = "sent_from_email",
  RECEIVED_AT_EMAIL = "received_at_email",
  SUBJECT_LINE = "subject_line",
  MESSAGE_BODY_MATCHES = "message_body_matches",
  AFTER_HOURS = "after_hours",
  TIME_BASED = "time_based",
  ASSIGNMENT_TYPE = "assignment_type",
  LAST_ORDER_TIME = "last_order_time",
  LAST_ORDER = "last_order",
  MESSAGE_SENTIMENT = "message_sentiment",
  HAS_TAG = "has_tag",
  RESPONSE_TIME = "response_time",
  SENT_FROM = "sent_from",
  RECEIVED_AT = "received_at",
  AI_STATUS = "ai_status",
  ASSIGNMENT = "assignment",
  MESSAGE_COUNT = "message_count",
}

/**
 * Trigger types where the subjectEntityId is a MessageId.
 * Used by backend logic (e.g. isMessageSubjectTrigger) to parse subjectEntityId as a MessageId.
 */
export const MESSAGE_TRIGGER_TYPES: readonly RuleTriggerType[] = [
  RuleTriggerType.CONVERSATION_MESSAGE_CREATED,
  RuleTriggerType.MERCHANT_MESSAGE_CREATED,
  RuleTriggerType.MESSAGE_RECEIVED,
];

/**
 * Trigger types that support message conditions in the UI.
 * A superset of MESSAGE_TRIGGER_TYPES — includes triggers whose initial message
 * is stored in triggerDetails.initialMessageId rather than subjectEntityId.
 */
export const MESSAGE_CONDITION_TRIGGER_TYPES: readonly RuleTriggerType[] = [
  ...MESSAGE_TRIGGER_TYPES,
  RuleTriggerType.CONVERSATION_CREATED,
  RuleTriggerType.THREAD_CREATED,
];

/**
 * Condition types that only apply when a message trigger is selected.
 */
export const MESSAGE_ONLY_CONDITION_TYPES: readonly RuleConditionType[] = [
  RuleConditionType.SENT_FROM_EMAIL,
  RuleConditionType.RECEIVED_AT_EMAIL,
  RuleConditionType.SENT_FROM,
  RuleConditionType.RECEIVED_AT,
  RuleConditionType.MESSAGE_BODY_MATCHES,
];

/**
 * Rule Action Types
 *
 * Defines the types of actions that can be executed when rule conditions pass.
 * Actions are executed sequentially in the order they appear in the rule.
 */
export enum RuleActionType {
  SET_STATUS = "SET_STATUS",
  ADD_TAG = "ADD_TAG",
  REMOVE_TAG = "REMOVE_TAG",
  AUTO_REPLY = "AUTO_REPLY",
  ASSIGN = "ASSIGN",
  ASSIGN_V3 = "ASSIGN_V3",
  ASSIGN_OLDEST_TICKET = "ASSIGN_OLDEST_TICKET",
  LIKE_COMMENT = "LIKE_COMMENT",
  HIDE_COMMENT = "HIDE_COMMENT",
  SET_STATUS_V3 = "SET_STATUS_V3",
  SET_PRIORITY = "SET_PRIORITY",
  START_AI_AGENT_EXECUTION = "START_AI_AGENT_EXECUTION",
  HANDOFF_AI = "HANDOFF_AI",
  // Backend-only action. Intentionally absent from RULE_V3_ACTION_TYPES so
  // it doesn't appear in the merchant-app rule editor dropdown.
  GENERATE_CONVERSATION_INSIGHTS = "GENERATE_CONVERSATION_INSIGHTS",
  // Backend-only action. Used by the default rule for sending CSAT surveys.
  SEND_CSAT_SURVEY = "SEND_CSAT_SURVEY",
  MERGE_SUPPORT_TICKET = "MERGE_SUPPORT_TICKET",
  SPLIT_SUPPORT_TICKET = "SPLIT_SUPPORT_TICKET",
  STOP_TOPIC_CONTINUATION = "STOP_TOPIC_CONTINUATION",
}

/**
 * Actions the V3 rule engine offers in its editor dropdown.
 *
 * This is the single source of truth for "which actions can a V3 rule
 * contain" and their user-facing labels. Adding a new V3 action = one
 * entry in `RULE_V3_ACTION_TYPES` plus its label in
 * `RULE_V3_ACTION_LABELS`.
 *
 * Note: legacy `RuleActionType.ASSIGN` is intentionally absent — V3 uses
 * `ASSIGN_V3` (labeled simply "Assign" in the UI). V2 rules continue to
 * use `ASSIGN` via their own editor.
 */
export const RULE_V3_ACTION_TYPES = [
  RuleActionType.SET_STATUS_V3,
  RuleActionType.SET_PRIORITY,
  RuleActionType.ADD_TAG,
  RuleActionType.REMOVE_TAG,
  RuleActionType.ASSIGN_V3,
  RuleActionType.ASSIGN_OLDEST_TICKET,
  RuleActionType.AUTO_REPLY,
  RuleActionType.START_AI_AGENT_EXECUTION,
  RuleActionType.HANDOFF_AI,
  RuleActionType.LIKE_COMMENT,
  RuleActionType.HIDE_COMMENT,
  RuleActionType.MERGE_SUPPORT_TICKET,
  RuleActionType.SPLIT_SUPPORT_TICKET,
  RuleActionType.STOP_TOPIC_CONTINUATION,
] as const satisfies readonly RuleActionType[];

export type RuleV3ActionType = (typeof RULE_V3_ACTION_TYPES)[number];

export const RULE_V3_ACTION_LABELS: Record<RuleV3ActionType, string> = {
  [RuleActionType.SET_STATUS_V3]: "Set status",
  [RuleActionType.SET_PRIORITY]: "Set priority",
  [RuleActionType.ADD_TAG]: "Add tag",
  [RuleActionType.REMOVE_TAG]: "Remove tag",
  [RuleActionType.ASSIGN_V3]: "Assign",
  [RuleActionType.ASSIGN_OLDEST_TICKET]: "Assign oldest ticket",
  [RuleActionType.AUTO_REPLY]: "Auto reply",
  [RuleActionType.START_AI_AGENT_EXECUTION]: "AI response",
  [RuleActionType.HANDOFF_AI]: "Handoff AI",
  [RuleActionType.LIKE_COMMENT]: "Like comment",
  [RuleActionType.HIDE_COMMENT]: "Hide comment",
  [RuleActionType.MERGE_SUPPORT_TICKET]: "Merge support ticket",
  [RuleActionType.SPLIT_SUPPORT_TICKET]: "Split conversation",
  [RuleActionType.STOP_TOPIC_CONTINUATION]: "Stop ticket continuation",
};

/**
 * Error Handling Strategies
 *
 * Defines how the rule engine should handle errors during action execution.
 */
/**
 * Rule Event Types
 *
 * Defines the payload shapes for each rule trigger event.
 */
export interface BaseRuleEvent {
  conversationId: string;
}

export interface ConversationStatusChangedEvent extends BaseRuleEvent {
  oldStatus: string;
  newStatus: string;
}

export interface TagAddedEvent extends BaseRuleEvent {
  tag: string;
}

export interface AssignmentChangedEvent extends BaseRuleEvent {
  oldAssigneeId?: string;
  newAssigneeId?: string;
}

export interface ChatConvertedToEmailEvent extends BaseRuleEvent {
  originalPlatform: string;
}

export interface TagRemovedEvent extends BaseRuleEvent {
  tag: string;
}

export interface ConversationPriorityChangedEvent extends BaseRuleEvent {
  oldPriority?: string;
  newPriority?: string;
}

export interface ConversationReadEvent extends BaseRuleEvent {
  readByUserId?: string;
}

export interface ThreadCreatedEvent extends BaseRuleEvent {
  threadId: string;
  channel: string;
}

export type RuleEventMap = Record<RuleTriggerType, BaseRuleEvent> & {
  [RuleTriggerType.CONVERSATION_CREATED]: BaseRuleEvent;
  [RuleTriggerType.CONVERSATION_MESSAGE_CREATED]: BaseRuleEvent;
  [RuleTriggerType.MERCHANT_MESSAGE_CREATED]: BaseRuleEvent;
  [RuleTriggerType.MESSAGE_RECEIVED]: BaseRuleEvent;
  [RuleTriggerType.CONVERSATION_STATUS_CHANGED]: ConversationStatusChangedEvent;
  [RuleTriggerType.TAG_ADDED]: TagAddedEvent;
  [RuleTriggerType.TAG_REMOVED]: TagRemovedEvent;
  [RuleTriggerType.ASSIGNMENT_CHANGED]: AssignmentChangedEvent;
  [RuleTriggerType.CONVERSATION_PRIORITY_CHANGED]: ConversationPriorityChangedEvent;
  [RuleTriggerType.CONVERSATION_READ]: ConversationReadEvent;
  [RuleTriggerType.THREAD_CREATED]: ThreadCreatedEvent;
  [RuleTriggerType.CHAT_CONVERTED_TO_EMAIL]: ChatConvertedToEmailEvent;
};

export enum ErrorHandlingStrategy {
  /**
   * Stop execution immediately if any action fails
   * Recommended for critical actions where partial execution is unsafe
   */
  FAIL_FAST = "fail_fast",

  /**
   * Continue executing remaining actions even if one fails
   * Failed actions are logged but don't stop the flow
   */
  CONTINUE_ON_ERROR = "continue_on_error",
}
